import { nanoid } from 'nanoid';
import * as React from 'react';
import type { FileRejection } from 'react-dropzone';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { compose } from 'redux';
import type { InjectedFormProps, FormErrorsWithArray } from 'redux-form';
import { reduxForm, Field, getFormValues, getFormSyncErrors, FieldArray } from 'redux-form';

import type { W_Job_FindUserGroupAndPMForInvoicing_VM } from 'ab-api/web/job/findUserGroupAndPMForInvoicing';

import type { PendingAttachmentVM } from 'ab-viewModels/attachment/pendingAttachment.viewModel';
import type { ContactForInvoicesVM } from 'ab-viewModels/contact/contactsForInvoices.viewModel';
import type InvoiceCodesVM from 'ab-viewModels/workRequest/invoiceCodes.viewModel';
import type InoviceReminderRecipientsVM from 'ab-viewModels/workRequest/invoiceReminderRecipients';

import { getId } from '@acceligentllc/shared/utils/array';
import { isValidEmail } from '@acceligentllc/shared/utils/email';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { BlobStorageContainer } from '@acceligentllc/shared/enums/blobStorageContainer';
import { FileType, IMAGE_FILE_TYPES } from '@acceligentllc/shared/enums/fileType';
import { InvoiceBillingLookupType } from '@acceligentllc/shared/enums/invoiceBillingLookupType';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import type { RootState } from 'af-reducers';

import { dollarFormatter } from 'af-utils/format.util';

import InvoiceStatusDisplay from 'ab-enums/invoiceStatusDisplay.enum';

import * as FORMS from 'af-constants/reduxForms';
import CLIENT from 'af-constants/routes/client';

import { Button, SegmentLabel, errorToast, loadingToast, successToast } from '@acceligentllc/storybook';

import Checkbox from 'af-fields/Checkbox';
import DateInput from 'af-fields/DateInput';
import Input from 'af-fields/Input';
import Textarea from 'af-fields/Textarea';

import Breadcrumbs from 'af-components/Breadcrumbs';
import ConfirmationModal from 'af-components/ConfirmationModal';
import FileUploadMessageWithTypes from 'af-components/Dropzone/FileUploadMessageWithTypes';
import DropzoneWithButton from 'af-components/DropzoneWithButton';
import type { RouteParams } from 'af-components/Header';
import LockedValue from 'af-components/LockedValue';
import SubmitButton from 'af-components/SubmitButton';

import * as AttachmentActions from 'af-actions/attachment';
import * as ContactActions from 'af-actions/contacts';
import * as JobActions from 'af-actions/jobs';

import { InvoiceFM } from './formModel';
import styles from './styles.module.scss';
import { JobPreviewTabRoutes } from '../../types';
import type { OwnProps as AttachmentArrayProps } from '../Attachments/AttachmentItems';
import AttachmentItems from '../Attachments/AttachmentItems';
import type { OwnProps as BillingContactArrayProps } from '../BillingContacts/BillingContactArray';
import BillingContactArray from '../BillingContacts/BillingContactArray';

type ValidationError = {
	[index: number]: { [key: string]: string; };
};

interface PathParams {
	jobId: string;
	invoiceId?: string;
}

type OwnProps = CustomRouteComponentProps<RouteParams & PathParams>;

type FormOwnProps = ConnectedProps<typeof connector> & OwnProps;
type Props = FormOwnProps & InjectedFormProps<InvoiceFM>;

const CreateOrEditInvoice: React.FC<Props> = (props) => {
	const {
		createInvoice,
		updateInvoice,
		getInvoiceById,
		getAllInvoiceCodesForJob,
		areAutomaticRemindersEnabled,
		uploadPendingAttachments,
		deletePendingAttachments,
		getInvoiceReminderRecipients,
		history,
		match: { params: { jobId, invoiceId } },
		location: { state: { orgAlias, jobCode, billingContactsToPrefill, backToAccountingPage } },
		companyName,
		initialize,
		currentFormValues,
		change,
		uploadProgress,
		isSendingInvoiceNotificationsEnabled,
		errors,
		initialized,
		currentUserFullName,
		findContactsForInvoices,
		handleSubmit,
		submitting,
		getUserGroupAndPM,
	} = props;

	const [automaticRemindersEnabled, setAutomaticRemindersEnabled] = React.useState<boolean>(false);
	const [showInvoiceModal, setShowInvoiceModal] = React.useState<boolean>(false);
	const [showLeaveModal, setShowLeaveModal] = React.useState<boolean>(false);
	const [invoicing, setInvoicing] = React.useState<boolean>(false);
	const [currentlyFilledForm, setCurrentlyFilledForm] = React.useState<Nullable<InvoiceFM>>(null);
	const [existingInvoiceCodes, setExistingInvoiceCodes] = React.useState<Nullable<InvoiceCodesVM[]>>([]);
	const [billingContacts, setBillingContacts] = React.useState<Nullable<ContactForInvoicesVM[]>>(null);
	const [isLoading, setIsLoading] = React.useState(false);
	const [additionalRecipients, setAdditionalRecipients] = React.useState<Nullable<InoviceReminderRecipientsVM[]>>(null);
	const [userGroupAndPM, setUserGroupAndPM] = React.useState<Nullable<W_Job_FindUserGroupAndPMForInvoicing_VM>>(null);

	const INVOICE_INVOICE_MODAL_BODY = React.useMemo(() => (
		<>
			You are about to change status of this draft to <i>Invoiced</i>.
			<br />
			Some sections of the form will no longer be editable.
			<br />
			<br />
			Continue with invoicing?
		</>
	), []);

	const getInvoiceModalBody = React.useCallback(() => {
		if (currentFormValues?.billingContactMethods?.length && currentFormValues.sendReminderOnInvoice) {
			return (<>
				You are about to change status of this draft to <i>Invoiced</i>.
				<br />
				You will not be able to make any changes to the data after it has been invoiced.
				<br />
				<br />
				Invoice information will be sent to following billing contacts:
				<br />
				{
					Object.values(
						currentFormValues.billingContactMethods.reduce((_acc, _contact) => {
							if (!_contact) {
								return _acc;
							}

							if (_contact.type === InvoiceBillingLookupType.CONTACT) {
								const key = _contact.contactId;

								if (!_acc[key]) {
									_acc[key] = { label: _contact.fullName, emails: [] };
								}

								_acc[key].emails.push(_contact.email);
							}

							if (_contact.type === InvoiceBillingLookupType.EMAIL_ONLY) {
								const key = `email-only-${_contact.email}`;

								_acc[key] = {
									label: _contact.email, emails: [],
								};
							}

							return _acc;
						}, {} as Record<string | number, { label: string; emails: string[]; }>)
					).map((_contact, _index, _array) => (
						<React.Fragment key={_index}>
							<b>{_contact.label}</b>
							{_contact.emails.length > 0 && (
								<span> ({_contact.emails.join(', ')})</span>
							)}
							{_index !== _array.length - 1 ? ', ' : '.'}
						</React.Fragment>
					))
				}
				{
					userGroupAndPM?.userGroupName &&
					<>
						< br />
						< br />
						user group: <b>{userGroupAndPM?.userGroupName}</b>
					</>
				}
				{
					userGroupAndPM?.pmFirstName &&
					<>
						< br />
						< br />
						and project manager: <b>{userGroupAndPM?.pmFirstName} {userGroupAndPM?.pmLastName} ({userGroupAndPM?.pmEmail})</b>
					</>
				}
				{
					!!additionalRecipients?.length &&
					<>
						< br />
						< br />
						and recipients: {additionalRecipients.map((_recipient, index) => {
							return <b key={index}>
								{` ${_recipient.firstName} ${_recipient.lastName} ${_recipient.email ? ` (${_recipient.email})` : ''}${additionalRecipients.length - 1 === index ? '' : ','}`}
							</b>;
						})}
					</>
				}
				< br />
				< br />
				To invoice without sending an email, uncheck this option in the
				< i > Reminders</i > section.
				< br />
				<br />
				Continue with invoicing?
			</>);
		}
		return INVOICE_INVOICE_MODAL_BODY;
	}, [INVOICE_INVOICE_MODAL_BODY, currentFormValues, userGroupAndPM, additionalRecipients]);

	const LEAVE_PAGE_MODAL_BODY = (
		<>
			You are about to leave this page without saving or invoicing.
			<br />
			Changes that you made will not be saved.
		</>
	);

	const ALLOWED_ATTACHMENT_FILE_TYPES_MIME = {
		'image/*': IMAGE_FILE_TYPES,
		'application/*': [
			FileType.PDF,
			FileType.XLSX,
			FileType.XLS,
			FileType.DOCX,
			FileType.DOC,
		],
		'text/*': [FileType.CSV],
	};

	const closeInvoiceModal = React.useCallback(() => setShowInvoiceModal(false), []);
	const closeLeaveModal = React.useCallback(() => setShowLeaveModal(false), []);

	const fetchAndInitializeData = React.useCallback(async () => {
		setIsLoading(true);
		const [
			invoiceCodes,
			areAutomaticInvoiceRemindersEnabledForCompany,
			fetchedContacts,
			additionalInvoiceRecipientContacts,
			usrGroupPM,
		] = await Promise.all([
			getAllInvoiceCodesForJob(+jobId),
			areAutomaticRemindersEnabled(),
			findContactsForInvoices(),
			getInvoiceReminderRecipients(+jobId),
			getUserGroupAndPM(+jobId),
		]);

		setExistingInvoiceCodes(invoiceCodes);
		setAutomaticRemindersEnabled(areAutomaticInvoiceRemindersEnabledForCompany);
		setBillingContacts(fetchedContacts);
		setAdditionalRecipients(additionalInvoiceRecipientContacts);
		setUserGroupAndPM(usrGroupPM);

		if (invoiceId !== undefined && jobId) {
			const invoiceVM = await getInvoiceById(+jobId, +invoiceId);
			if (!invoiceVM) {
				throw new Error('Invoice not found');
			}
			initialize(InvoiceFM.fromVMtoFM(invoiceVM));
		} else {
			initialize({
				status: InvoiceStatusDisplay.DRAFT,
				excludeInternalUsersFromAutomaticReminders: false,
				excludeBillingContactsFromAutomaticReminders: false,
				sendReminderOnInvoice: true,
				billingContactMethods: !!billingContactsToPrefill?.emails?.length ? [{
					id: billingContactsToPrefill.selectedContactMethodIds[0],
					type: InvoiceBillingLookupType.CONTACT,
					email: !!billingContactsToPrefill.selectedContactMethodEmails[0] ? billingContactsToPrefill.selectedContactMethodEmails[0] : [],
					contactId: billingContactsToPrefill.contactId,
					title: billingContactsToPrefill.title,
					fullName: billingContactsToPrefill.fullName,
					companyName: billingContactsToPrefill.companyName,
					contactMethodId: billingContactsToPrefill.selectedContactMethodIds[0],
					lastReminderSent: null,
				}] : [],
			});
		}
		setIsLoading(false);
	}, [getAllInvoiceCodesForJob,
		jobId,
		areAutomaticRemindersEnabled,
		findContactsForInvoices,
		invoiceId,
		getInvoiceById,
		initialize,
		billingContactsToPrefill,
		getInvoiceReminderRecipients,
		getUserGroupAndPM,
	]);

	// in order to be used only once the component is mounted, deps are left empty
	React.useEffect(() => {
		fetchAndInitializeData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const clearPendingAttachments = React.useCallback(async () => {
		if (currentFormValues?.uploadedAttachmentIds?.length) {
			await deletePendingAttachments({ attachmentIds: currentFormValues?.uploadedAttachmentIds });
		}
	}, [currentFormValues?.uploadedAttachmentIds, deletePendingAttachments]);

	const onBack = React.useCallback(() => {
		setShowLeaveModal(true);
	}, []);

	const goBackToTable = React.useCallback(() => {
		clearPendingAttachments();
		if (backToAccountingPage) {
			history.push({
				pathname: CLIENT.COMPANY.ACCOUNTING.INVOICES.TABLE(orgAlias, companyName),
				state: {
					orgAlias,
				},
			});
		} else {
			history.push({
				pathname: CLIENT.COMPANY.JOBS.PREVIEW_TAB(orgAlias, companyName, jobId, JobPreviewTabRoutes.INVOICES),
				state: {
					orgAlias,
				},
			});
		}
	}, [backToAccountingPage, clearPendingAttachments, companyName, history, jobId, orgAlias]);

	const onSubmit = React.useCallback(async () => {
		if (currentFormValues.id) {
			await updateInvoice(+jobId, currentFormValues.id, InvoiceFM.toUpdateInvoiceRM(currentFormValues));
		} else {
			await createInvoice(+jobId, InvoiceFM.toCreateInvoiceRM(currentFormValues));
		}
		goBackToTable();
	}, [createInvoice, currentFormValues, goBackToTable, jobId, updateInvoice]);

	const onInvoice = React.useCallback(async () => {
		setCurrentlyFilledForm(currentFormValues);
		setShowInvoiceModal(true);
	}, [currentFormValues]);

	const invoiceDraft = React.useCallback(async () => {
		if (!currentlyFilledForm) {
			throw new Error('Missing form values');
		}
		setInvoicing(true);
		currentlyFilledForm.status = InvoiceStatusDisplay.INVOICED;
		currentlyFilledForm.invoicingDate = TimeUtils.formatDate(new Date(), TimeFormat.DATE_ONLY);
		onSubmit();
		setCurrentlyFilledForm(null);
	}, [currentlyFilledForm, onSubmit]);

	const invoiceStatusDraftValue = React.useMemo(() => {
		return (
			<span className={`${styles['invoice-form__status-cell']} ${styles['invoice-form__status-cell--draft']} `}>
				<span className="icon-draft" />
				<b>Draft</b>
			</span>
		);
	}, []);

	const handleAttachmentsUpload = React.useCallback((attachmentIds: number[]) => {
		change('uploadedAttachmentIds', [...attachmentIds]);
	}, [change]);

	const handleAttachmentDeleted = React.useCallback((attachmentId: number) => {
		const indexOfAttachmentId = currentFormValues.uploadedAttachmentIds?.findIndex((_id) => _id === attachmentId);
		if (indexOfAttachmentId !== undefined && indexOfAttachmentId !== -1 && currentFormValues.uploadedAttachmentIds) {
			const nextAttachmentIds = [...currentFormValues.uploadedAttachmentIds];
			nextAttachmentIds.splice(indexOfAttachmentId, 1);
			change('uploadedAttachmentIds', nextAttachmentIds);
		}
	}, [change, currentFormValues]);

	const handleAttachmentsChanged = React.useCallback((currentAttachments: InvoiceFM['attachments'], changedAttachments: InvoiceFM['attachments']) => {
		const addedAttachments: InvoiceFM['attachments'] = [...currentAttachments];
		// UGLY but needed. Simple table Field component cannot handle bulk change.
		// this was written as a workaround.
		changedAttachments.forEach((_att) => {
			addedAttachments.push(_att);
			change('attachments', addedAttachments);
		});
	}, [change]);

	const handleAttachmentUploadStart = React.useCallback((correlationId: string) => {
		const nextUploadingAttachments = { ...currentFormValues.uploadingAttachments };
		nextUploadingAttachments[correlationId] = true;
		change('uploadingAttachments', nextUploadingAttachments);
	}, [change, currentFormValues]);

	const handleAttachmentUploadEnd = React.useCallback((correlationId: string) => {
		const nextUploadingAttachments = { ...currentFormValues.uploadingAttachments };
		delete nextUploadingAttachments[correlationId];
		change('uploadingAttachments', nextUploadingAttachments);
	}, [change, currentFormValues]);

	const handleFileUpload = React.useCallback(async (acceptedFiles: File[], fileRejections: FileRejection[]) => {

		if (fileRejections.length > 0) {
			let message = `Uploading of ${fileRejections[0].file.name} failed.`;
			if (fileRejections[0].errors[0].code === 'file-invalid-type') {
				message = 'Uploading failed - File type not supported';
			}
			errorToast(fileRejections[0].file.name, { title: message, timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG) });
			return;
		}

		const correlationId = nanoid();
		handleAttachmentUploadStart?.(correlationId);
		let uploadedAttachments: PendingAttachmentVM[] = [];

		const msgBase = acceptedFiles.length > 1
			? `${acceptedFiles[0].name} and ${acceptedFiles.length - 1} other file${acceptedFiles.length > 2 ? 's' : ''}`
			: acceptedFiles[0].name;

		const startingTimestamp = TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG);
		loadingToast(acceptedFiles[0].name, { title: msgBase, timestamp: startingTimestamp });

		try {
			uploadedAttachments = await uploadPendingAttachments({ requestFiles: acceptedFiles }, BlobStorageContainer.INVOICES_ATTACHMENTS);
			successToast(acceptedFiles[0].name, { title: `${msgBase} has successfully been uploaded.`, timestamp: startingTimestamp });
		} finally {
			handleAttachmentUploadEnd?.(correlationId);
		}

		const nextUploadedAttachmentIds = [...(currentFormValues.uploadedAttachmentIds ?? []), ...uploadedAttachments.map(getId)];
		const nextUploadedAttachments = uploadedAttachments.map((_att) => ({ ..._att, fileName: _att.name, uploadedBy: { fullName: currentUserFullName } }));
		handleAttachmentsChanged(currentFormValues.attachments ?? [], nextUploadedAttachments);
		handleAttachmentsUpload(nextUploadedAttachmentIds);
	}, [
		currentFormValues,
		handleAttachmentUploadEnd,
		handleAttachmentUploadStart,
		handleAttachmentsChanged,
		handleAttachmentsUpload,
		uploadPendingAttachments,
		currentUserFullName,
	]);

	const uploading = uploadProgress !== 100 && uploadProgress !== 0;

	const style: Partial<React.CSSProperties> | undefined = uploading
		? { marginTop: '15px', padding: '1px', background: `linear-gradient(to right, #9FCAFF ${uploadProgress}%, transparent ${uploadProgress}% 100%)` }
		: { marginTop: '15px' };

	const isSaveFormEnabled = React.useMemo(() => {
		return InvoiceFM.saveFormEnabled(currentFormValues, errors);
	}, [currentFormValues, errors]);

	const checkUniqueInvoiceCode = React.useCallback((value: string) => {

		if (!value) {
			return 'Invoice ID is required';
		}

		if (!existingInvoiceCodes) {
			return true;
		}

		if (existingInvoiceCodes.some((_item) => _item.invoiceCode === value && (!invoiceId || +invoiceId !== _item.id))) {
			return 'Invoice ID already taken';
		}
	}, [existingInvoiceCodes, invoiceId]);

	const currentFormBillingContactMethodsErrors = React.useMemo(() => {
		const emailValidation: ValidationError = {};

		if (currentFormValues?.billingContactMethods) {
			for (let i = 0; i < currentFormValues.billingContactMethods.length; i++) {
				const _bcm = currentFormValues.billingContactMethods[i];

				if (!_bcm) {
					emailValidation[i] = { 'fullName': 'No Contact selected' };
				} else if (_bcm.email !== '' && !_bcm.email.includes('@')) {
					emailValidation[i] = { 'fullName': 'Please use email format: user.name@email.com' };
				} else if (_bcm.email !== '' && !isValidEmail(_bcm.email)) {
					emailValidation[i] = { 'fullName': 'Email not valid' };
				} else {
					emailValidation[i] = {};
				}
			}
		}
		return emailValidation;
	}, [currentFormValues]);

	const jobBillingContacts = React.useMemo(() => {
		if (currentFormValues?.billingContactMethods) {
			return currentFormValues?.billingContactMethods;
		}
		return [];
	}, [currentFormValues]);

	const handleDateCreatedChange = React.useCallback((e) => {
		const date = TimeUtils.formatDate(
			TimeUtils.addDays(e.target.value, 30, TimeFormat.DATE_ONLY),
			TimeFormat.DATE_ONLY
		);
		change('dueDate', date);
	}, [change]);

	const manualReminderTooltipMessage =
		<>
			<strong>Sending manual reminder</strong> is disabled until the form has been invoiced and all changes have been saved.
		</>;

	if (isLoading) {
		return (
			<Breadcrumbs
				items={[
					{ label: 'Jobs', url: CLIENT.COMPANY.JOBS.TABLE(orgAlias, companyName) },
					{ label: jobCode, url: CLIENT.COMPANY.JOBS.PREVIEW(orgAlias, companyName, jobId) },
					{ label: 'Invoices', onClick: goBackToTable },
					{ label: 'Loading' },
				]}
			/>
		);
	}

	return (

		<>

			<Breadcrumbs
				items={[
					{ label: 'Jobs', url: CLIENT.COMPANY.JOBS.TABLE(orgAlias, companyName) },
					{ label: jobCode, url: CLIENT.COMPANY.JOBS.PREVIEW(orgAlias, companyName, jobId) },
					{ label: 'Invoices', onClick: goBackToTable },
					{ label: invoiceId ? 'Edit' : 'New Invoice' },
				]}
			/>
			<div className={styles['invoice-form__tab-content__form']}>
				<div className={styles['invoice-form__submit-section']}>
					<div className={styles['invoice-form__submit-section__invoice-code-and-reminder']}>
						<span className={styles['invoice-form__submit-section__invoice-code-and-reminder__code']}>
							{currentFormValues?.invoiceCode ?? 'NEW INVOICE'}
						</span>

						{
							isSendingInvoiceNotificationsEnabled &&
							<Button
								disabled={true}
								icon="Email"
								label="Send Manual Reminder"
								onClick={onBack}
								style="secondary"
								tooltip={manualReminderTooltipMessage}
								tooltipPosition="right"
							/>
						}
					</div>
					<div className={styles.buttonActions}>
						<Button
							label="Back"
							onClick={onBack}
							style="secondary"
						/>
						<SubmitButton
							disabled={!isSaveFormEnabled}
							label="Save"
							onClick={handleSubmit(onSubmit)}
							reduxFormSubmitting={!invoicing && !!submitting}
						/>
						<SubmitButton
							disabled={!isSaveFormEnabled}
							label="Invoice"
							onClick={handleSubmit(onInvoice)}
							reduxFormSubmitting={invoicing}
						/>
					</div>
				</div>
				<div className={styles['invoice-form__tab-content__form__section']}>
					<div className="m-b-m">
						<SegmentLabel
							label="General Info"
							tooltip="Invoice details including the invoice ID, creation and due dates, current status, and any additional notes relevant to this invoice. The due date is automatically set to 30 days after the creation date."
						/>
					</div>
					<div className={styles['invoice-form__tab-content__form__section__row']}>
						<div className={styles['invoice-form__tab-content__form__section__row__column-id']}>
							<Field
								component={Input}
								id="invoiceCode"
								isRequired={true}
								label="Invoice ID"
								name={InvoiceFM.getAttributeName('invoiceCode')}
								placeholder="Invoice ID"
								required={true}
								type="text"
								validate={checkUniqueInvoiceCode}
							/>
						</div>
						<div className={styles['invoice-form__tab-content__form__section__row__column-date']}>
							<Field
								component={DateInput}
								dateFormat={TimeFormat.DATE_ONLY}
								id="dateCreated"
								isRequired={true}
								label="Date Created"
								name={InvoiceFM.getAttributeName('dateCreated')}
								onChange={handleDateCreatedChange}
								originalDateFormat={TimeFormat.DATE_ONLY}
								placeholderText={TimeFormat.DATE_ONLY}
								required={true}
							/>
						</div>
						<div className={styles['invoice-form__tab-content__form__section__row__column-date']}>
							<Field
								component={DateInput}
								dateFormat={TimeFormat.DATE_ONLY}
								id="dueDate"
								isRequired={true}
								label="Due Date"
								name={InvoiceFM.getAttributeName('dueDate')}
								originalDateFormat={TimeFormat.DATE_ONLY}
								placeholderText={TimeFormat.DATE_ONLY}
								required={true}
							/>
						</div>
						<div className={styles['invoice-form__tab-content__form__section__row__column-date']}>
							<LockedValue label="Invoice Status" value={invoiceStatusDraftValue} />

						</div>

					</div>
					<div className={styles['invoice-form__tab-content__form__section__row']}>
						<div className={styles['invoice-form__tab-content__form__section__row__column-note']}>
							<Field
								component={Textarea}
								id="note"
								label="Note"
								maxCharacters={300}
								name={InvoiceFM.getAttributeName('note')}
								rows={1}
								type="text"
							/>
						</div>
					</div>
				</div>
				<div className={styles['invoice-form__tab-content__form__section']}>
					<div className="m-b-m">
						<SegmentLabel
							label="BILLING CONTACTS"
							tooltip="Contacts designated to receive this invoice. The Billing Contact from Job Details is included here by default."
						/>
					</div>
					<FieldArray<BillingContactArrayProps>
						availableBillingContacts={billingContacts ?? []}
						billingContactMethods={jobBillingContacts}
						change={change}
						component={BillingContactArray}
						emailReminderSent={false}
						name={InvoiceFM.getAttributeName('billingContactMethods')}
						rerenderOnEveryChange={true}
						validationErrors={currentFormBillingContactMethodsErrors}
					/>
				</div>
				{
					isSendingInvoiceNotificationsEnabled &&
					<div className={styles['invoice-form__tab-content__form__section']}>
						<div className="m-b-m">
							<SegmentLabel
								label="REMINDERS"
								tooltip="Automatic email notification settings for this invoice, controlling whether billing contacts and internal users receive reminders when the invoice is issued or overdue."
							/>
						</div>
						<Field
							component={Checkbox}
							inline={true}
							label="Send email to billing contacts when invoicing"
							name="sendReminderOnInvoice"
							tooltipMessage="Pay attention that if there are no billing contacts listed above, emails still won't be send even if this checkbox is selected."
						/>
						{
							automaticRemindersEnabled &&
							<>
								<Field
									component={Checkbox}
									inline={true}
									label="Exclude internal users from automatic reminders"
									name="excludeInternalUsersFromAutomaticReminders"
									tooltipMessage="User group, project manager and invoice reminder recipients defined on job level."

								/>
								<Field
									component={Checkbox}
									inline={true}
									label="Exclude billing contacts from automatic reminders"
									name="excludeBillingContactsFromAutomaticReminders"
								/>
							</>
						}
					</div>
				}
				<div className={styles['invoice-form__tab-content__form__section']}>
					<div className="m-b-m">
						<SegmentLabel
							label="PAYMENT INFORMATION"
							tooltip="Financial summary of this invoice: total amount, payments received, outstanding balance, and retained amount. Total Paid and Outstanding Balance are auto-calculated from the invoice total and installments."
						/>
					</div>
					<div className={styles['invoice-form__tab-content__form__section__row']}>
						<div className={styles['invoice-form__tab-content__form__section__row__column-payment-info']}>
							<Field
								addonAfter="$"
								component={Input}
								id="totalAmount"
								isRequired={true}
								isZeroIfEmpty={true}
								label="Total"
								name={InvoiceFM.getAttributeName('totalAmount')}
								required={true}
								type="number"
								withThousandsSeparator={true}
							/>
						</div>
						<div className={styles['invoice-form__tab-content__form__section__row__column-payment-info']}>
							<LockedValue label="Total Paid" value={dollarFormatter.format(0)} />
						</div>
						<div className={styles['invoice-form__tab-content__form__section__row__column-payment-info']}>
							<LockedValue label="Outstanding" value={dollarFormatter.format(currentFormValues?.totalAmount ?? 0)} />
						</div>
						<div className={styles['invoice-form__tab-content__form__section__row__column-payment-info']}>
							<Field
								addonAfter="$"
								component={Input}
								id="retainedAmount"
								label="Retained Amount"
								name={InvoiceFM.getAttributeName('retainedAmount')}
								type="number"
								withThousandsSeparator={true}
							/>
						</div>
					</div>
				</div>
				<div className={styles['invoice-form__tab-content__form__section']}>
					<div className="m-b-m">
						<SegmentLabel
							label="INSTALLMENTS"
							tooltip="A record of all partial payments associated with this invoice, including the amount, date paid, and any notes per installment."
						/>
					</div>
					<div className={styles['invoice-form__tab-content__form__section__installment-notice']}>
						Draft needs to be invoiced in order to add installments.
					</div>
				</div>
				<div className={styles['invoice-form__tab-content__form__section']}>
					<div className="m-b-m">
						<SegmentLabel
							label="ATTACHMENTS"
							tooltip="All attachments associated with this invoice. They get sent in email reminders."
						/>
					</div>
					<FieldArray<AttachmentArrayProps>
						component={AttachmentItems}
						errors={errors}
						initialized={initialized}
						name="attachments"
						onDelete={handleAttachmentDeleted}
						rerenderOnEveryChange={true}
					/>
					<DropzoneWithButton
						allowedTypes={ALLOWED_ATTACHMENT_FILE_TYPES_MIME}
						onDrop={handleFileUpload}
						style={style}
					>
						<FileUploadMessageWithTypes
							uploading={uploading}
						/>
					</DropzoneWithButton>
				</div>
			</div>
			<ConfirmationModal
				body={getInvoiceModalBody()}
				closeModal={closeInvoiceModal}
				closeText="Cancel"
				confirmAction={invoiceDraft}
				confirmText="Continue"
				modalStyle="info"
				showModal={showInvoiceModal}
				size="md"
				title="Invoice draft?"
			/>
			<ConfirmationModal
				body={LEAVE_PAGE_MODAL_BODY}
				closeModal={closeLeaveModal}
				closeText="Stay"
				confirmAction={goBackToTable}
				confirmText="Leave"
				modalStyle="danger"
				showModal={showLeaveModal}
				size="md"
				title="Leave Page?"
			/>
		</>
	);
};

function mapStateToProps(state: RootState) {
	const { user: { companyData, userData } } = state;

	if (!userData || !companyData) {
		throw new Error('User not logged in');
	}

	const currentFormValues = getFormValues(FORMS.INVOICE_CREATE_FORM)(state) as InvoiceFM;

	const errors = getFormSyncErrors(FORMS.INVOICE_CREATE_FORM)(state) as FormErrorsWithArray<InvoiceFM, string>;

	const currentUserFullName = `${userData.firstName} ${userData.lastName}`;

	return {
		currentUserFullName,
		companyName: companyData.name,
		isSendingInvoiceNotificationsEnabled: companyData.isSendingInvoiceNotificationsEnabled,
		currentFormValues,
		errors,
		uploadProgress: state.http.uploadProgress,
	};
}

function mapDispatchToProps() {
	return {
		createInvoice: JobActions.createInvoice,
		updateInvoice: JobActions.updateInvoice,
		getInvoiceById: JobActions.getInvoiceById,
		getAllInvoiceCodesForJob: JobActions.getAllInvoiceCodesForJob,
		areAutomaticRemindersEnabled: JobActions.areAutomaticRemindersEnabled,
		getInvoiceReminderRecipients: JobActions.getInvoiceReminderRecipients,
		getUserGroupAndPM: JobActions.getUserGroupAndPM,
		uploadPendingAttachments: AttachmentActions.uploadPendingAttachments,
		deletePendingAttachments: AttachmentActions.deletePendingAttachments,
		findContactsForInvoices: ContactActions.findForInvoices,
	};
}

const formConnector = reduxForm<InvoiceFM>({
	form: FORMS.INVOICE_CREATE_FORM,
	validate: InvoiceFM.validate,
});

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentClass<OwnProps>>(
	formConnector,
	connector
);
export default enhance(CreateOrEditInvoice);
